import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

/** Valore del filtro d'Origine (vedi **Origine** in CONTEXT.md): ``''`` è
 *  "Originali/Compatibili", il predefinito, e mostra tutto. Non esiste "solo
 *  originali". */
export type Origine = '' | 'compatibile';

/** Il filtro letto dall'URL: ``origine=originale`` (link vecchi) si ignora. */
export function origineDaUrl(v: string | null): Origine {
  return v === 'compatibile' ? 'compatibile' : '';
}

export interface FacetCategory { slug: string; label: string }

export interface FacetsResponse {
  brands: string[];
  categories: FacetCategory[];
  gruppi: FacetCategory[];
  origins?: Array<'originale' | 'compatibile'>;
}

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Carica in cascata le opzioni disponibili per i filtri di ricerca
 * (marca → tipologia → origine) via ``/api/products/facets``. Condiviso da
 * tutte le pagine con barra filtri orizzontale (ricerca prodotti, outlet,
 * widget "Ricerca avanzata" in home).
 *
 * ``onTipologiaNonPiuValida`` azzera la tipologia quando, cambiata la marca,
 * il valore scelto non è più tra le opzioni: lasciarlo attivo darebbe zero
 * risultati senza far capire perché. Sta qui e non nelle pagine perché era
 * ricopiato in tre punti con condizioni leggermente diverse fra loro.
 */
export function useProductFacets(
  marca: string,
  tipologia: string,
  opts: {
    onTipologiaNonPiuValida?: () => void;
    /** false nei pannelli admin: lì si gestiscono anche i prodotti non
     *  pubblicati, e le opzioni devono rispecchiare quello che si vede. */
    publicOnly?: boolean;
  } = {},
) {
  const { onTipologiaNonPiuValida, publicOnly = true } = opts;
  const [facets, setFacets] = useState<FacetsResponse>({ brands: [], categories: [], gruppi: [], origins: [] });
  const [loadingFacets, setLoadingFacets] = useState(true);
  // Per quale selezione sono le facet in ``facets``: finché la risposta per
  // la selezione corrente non arriva, le origini sono di quella precedente.
  const selezione = `${marca}\u0000${tipologia}\u0000${publicOnly}`;
  const [facetsPer, setFacetsPer] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoadingFacets(true);
    const params: Record<string, unknown> = { publicOnly, includeModels: false };
    if (marca) params.brand = marca;
    if (tipologia) params.category = tipologia;
    axios
      .get<FacetsResponse>(`${API}/api/products/facets`, { params })
      .then(res => {
        if (cancelled) return;
        setFacets(prev => ({
          brands: res.data.brands.length >= prev.brands.length ? res.data.brands : prev.brands,
          categories: res.data.categories,
          gruppi: res.data.gruppi || [],
          origins: res.data.origins || [],
        }));
        setFacetsPer(selezione);
        // Accetto sia gruppo che sottogruppo: i link legacy (footer, pagine
        // categoria) puntano a slug di sottogruppo e devono restare validi.
        if (tipologia && onTipologiaNonPiuValida) {
          const valido = (res.data.gruppi || []).some(g => g.slug === tipologia)
            || res.data.categories.some(c => c.slug === tipologia);
          if (!valido) onTipologiaNonPiuValida();
        }
      })
      .catch(() => { if (!cancelled) setFacets(prev => ({ ...prev, categories: [], gruppi: [], origins: [] })); })
      .finally(() => { if (!cancelled) setLoadingFacets(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marca, tipologia, publicOnly]);

  // "Originali/Compatibili" è il valore vuoto della select (sempre presente,
  // vedi ``ORIGINE_PREDEFINITA_LABEL``); qui c'è solo "Solo compatibili",
  // quando la selezione ne ha.
  const origineOptions = useMemo(() => {
    const available = facets.origins || [];
    const compatibili = [{ value: 'compatibile' as const, label: 'Solo compatibili' }];
    if (!marca && !tipologia) return compatibili;
    return available.includes('compatibile') ? compatibili : [];
  }, [facets.origins, marca, tipologia]);

  /** Origini della selezione corrente, ``null`` finché non sono arrivate. */
  const originiSelezione = facetsPer === selezione ? facets.origins || [] : null;

  // Opzioni del filtro Tipologia mostrate nella barra orizzontale: stessa
  // lista aggregata (GRUPPI merceologici, 30 valori) del widget "Ricerca
  // avanzata" in homepage — richiesta utente 25/08/2026: le due liste
  // "Tipologia" devono coincidere per non confondere l'utente con due
  // tassonomie diverse (prima qui si usavano i 55 sottogruppi granulari).
  // I link legacy di navigazione (footer, pagine categoria) continuano a
  // puntare a slug di sottogruppo — se il valore attivo è uno di questi e
  // non è tra i gruppi, lo aggiungiamo in testa alla lista cosi il filtro
  // resta popolato/selezionato invece di apparire vuoto.
  const tipologiaOptions = useMemo(() => {
    const opts = facets.gruppi.slice();
    if (tipologia && !opts.some(o => o.slug === tipologia)) {
      const sottogruppo = facets.categories.find(c => c.slug === tipologia);
      if (sottogruppo) opts.unshift(sottogruppo);
    }
    return opts;
  }, [facets.categories, facets.gruppi, tipologia]);

  return { facets, loadingFacets, origineOptions, tipologiaOptions, originiSelezione };
}

export const ORIGINE_PREDEFINITA_LABEL = 'Originali/Compatibili';

export interface StatoOrigine {
  value: Origine;
  /** true se "Solo compatibili" l'ha messo il filtro, non l'utente. */
  auto: boolean;
}

const PREDEFINITA: StatoOrigine = { value: '', auto: false };

/**
 * Il filtro d'Origine dopo che le origini della selezione (marca,
 * tipologia, …) sono note:
 * - selezione senza prodotti (nessuna origine) → predefinito;
 * - selezione senza originali → "Solo compatibili", messo dal filtro;
 * - di nuovo con originali → torna al predefinito se l'aveva messo il
 *   filtro, resta se l'ha scelto l'utente (salvo che non ci siano
 *   compatibili: l'opzione non c'è più).
 * ``origini`` ``null`` (non ancora caricate) lascia tutto com'è.
 */
export function riconciliaOrigine(stato: StatoOrigine, origini: readonly string[] | null): StatoOrigine {
  if (origini === null) return stato;
  if (origini.length === 0) return stato.value === '' && !stato.auto ? stato : PREDEFINITA;
  if (!origini.includes('originale')) {
    return stato.value === '' ? { value: 'compatibile', auto: true } : stato;
  }
  if (stato.value === 'compatibile' && (stato.auto || !origini.includes('compatibile'))) return PREDEFINITA;
  return stato;
}

/**
 * Stato del filtro d'Origine, uguale in homepage, catalogo e Outlet.
 * ``origini`` è ``originiSelezione`` di ``useProductFacets``.
 */
export function useOrigine(origini: readonly string[] | null) {
  const [stato, setStato] = useState<StatoOrigine>(PREDEFINITA);

  useEffect(() => {
    setStato(s => riconciliaOrigine(s, origini));
  }, [origini, stato]);

  /** Scelta dell'utente (o azzeramento): resta anche se tornano gli originali. */
  const scegliOrigine = useCallback((value: Origine) => setStato({ value, auto: false }), []);
  /** Valore arrivato dall'URL: se è già quello corrente, non cambia chi l'ha
   *  messo (un "Solo compatibili" del filtro finito nell'URL con Cerca). */
  const allineaOrigine = useCallback(
    (value: Origine) => setStato(s => (s.value === value ? s : { value, auto: false })),
    [],
  );

  return { origine: stato.value, scegliOrigine, allineaOrigine };
}
